import React, { useState, useEffect } from 'react';
import api from './api';
import useIsMobile from './hooks/useIsMobile';

const RaceStories = ({ raceId, raceName, user, onViewProfile, onLoginRequired }) => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', photo_url: '' });
  const [saving, setSaving] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    fetchStories();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raceId]);

  const fetchStories = async () => {
    try {
      const url = raceId ? `/stories?race_id=${raceId}` : '/stories';
      const res = await api.get(url);
      setStories(res.data);
    } catch (err) {
      console.error('Error loading stories:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.content.trim()) return;
    setSaving(true);
    try {
      if (editingStory) {
        await api.put(`/stories/${editingStory.id}`, form);
      } else {
        await api.post('/stories', { ...form, race_id: raceId || null });
      }
      setShowCompose(false);
      setEditingStory(null);
      setForm({ title: '', content: '', photo_url: '' });
      fetchStories();
    } catch (err) {
      console.error('Error saving story:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (storyId) => {
    if (!window.confirm('Delete this story?')) return;
    try {
      await api.delete(`/stories/${storyId}`);
      setStories(prev => prev.filter(s => s.id !== storyId));
    } catch (err) {
      console.error('Error deleting story:', err);
    }
  };

  const startEdit = (story) => {
    setEditingStory(story);
    setForm({ title: story.title, content: story.content, photo_url: story.photo_url || '' });
    setShowCompose(true);
  };

  const getUserName = (story) => {
    if (story.first_name) return `${story.first_name}${story.last_name ? ' ' + story.last_name : ''}`;
    return story.author_name || 'Runner';
  };

  const formatTimeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) {
    return <p style={{ color: '#8b9cb3', textAlign: 'center', padding: '2rem' }}>Loading stories...</p>;
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h3 style={{ color: '#FFFFFF', margin: 0, fontSize: '1.5rem' }}>Race Stories</h3>
        {user && (
          <button
            onClick={() => {
              setEditingStory(null);
              setForm({ title: '', content: '', photo_url: '' });
              setShowCompose(true);
            }}
            style={{
              padding: '0.5rem 1.25rem',
              background: '#FF8A3D',
              border: 'none',
              color: '#0B0B10',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.9rem'
            }}
          >
            Share Your Story
          </button>
        )}
      </div>

      {/* Compose / Edit Form */}
      {showCompose && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 138, 61, 0.3)',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <h4 style={{ color: '#FFFFFF', margin: '0 0 1rem 0' }}>
            {editingStory ? 'Edit Story' : 'Share Your Race Story'}
          </h4>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Story title (optional)"
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 138, 61, 0.3)',
              borderRadius: '8px',
              color: '#FFFFFF',
              fontSize: '1rem',
              outline: 'none',
              marginBottom: '0.75rem',
              boxSizing: 'border-box'
            }}
          />
          <textarea
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            placeholder="Tell your race story... What was the experience like? What moments stood out?"
            rows={5}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 138, 61, 0.3)',
              borderRadius: '8px',
              color: '#FFFFFF',
              fontSize: '1rem',
              outline: 'none',
              resize: 'vertical',
              fontFamily: 'inherit',
              marginBottom: '0.75rem',
              boxSizing: 'border-box'
            }}
          />
          <input
            type="url"
            value={form.photo_url}
            onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
            placeholder="Photo URL (optional)"
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 138, 61, 0.3)',
              borderRadius: '8px',
              color: '#FFFFFF',
              fontSize: '0.9rem',
              outline: 'none',
              marginBottom: '1rem',
              boxSizing: 'border-box'
            }}
          />
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={handleSubmit}
              disabled={saving || !form.content.trim()}
              style={{
                padding: '0.6rem 1.5rem',
                background: saving || !form.content.trim() ? '#555' : '#FF8A3D',
                border: 'none',
                color: '#0B0B10',
                borderRadius: '8px',
                cursor: saving || !form.content.trim() ? 'not-allowed' : 'pointer',
                fontWeight: 600
              }}
            >
              {saving ? 'Saving...' : editingStory ? 'Update' : 'Publish Story'}
            </button>
            <button
              onClick={() => { setShowCompose(false); setEditingStory(null); }}
              style={{
                padding: '0.6rem 1.5rem',
                background: 'transparent',
                border: '1px solid rgba(255, 138, 61, 0.3)',
                color: '#9ca3af',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Stories List */}
      {stories.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          background: 'rgba(255, 255, 255, 0.03)',
          borderRadius: '12px',
          border: '1px solid rgba(255, 138, 61, 0.15)'
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>{'📖'}</div>
          <p style={{ color: '#9ca3af', marginBottom: '0.5rem' }}>No stories yet</p>
          <p style={{ color: '#8b9cb3', fontSize: '0.9rem' }}>
            {user ? 'Be the first to share your race experience!' : 'Sign in to share your story.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {stories.map(story => (
            <div key={story.id} style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 138, 61, 0.15)',
              borderRadius: '12px',
              padding: isMobile ? '1rem' : '1.5rem',
              transition: 'border-color 0.2s'
            }}>
              {/* Author header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <div
                  onClick={() => onViewProfile && onViewProfile(story.author_id)}
                  style={{
                    width: '36px', height: '36px', borderRadius: '50%',
                    background: story.profile_photo ? `url(${story.profile_photo}) center/cover` : 'linear-gradient(135deg, #FF8A3D 0%, #C23AFF 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#FFFFFF', fontWeight: 700, fontSize: '0.85rem',
                    cursor: 'pointer', flexShrink: 0
                  }}
                >
                  {!story.profile_photo && getUserName(story).charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <span
                    onClick={() => onViewProfile && onViewProfile(story.author_id)}
                    style={{ color: '#FFFFFF', fontWeight: 600, cursor: 'pointer', fontSize: '0.95rem' }}
                  >
                    {getUserName(story)}
                  </span>
                  {story.verified_athlete && (
                    <span style={{ color: '#4ade80', fontSize: '0.75rem', marginLeft: '0.4rem' }}>{'✓'}</span>
                  )}
                  <div style={{ color: '#8b9cb3', fontSize: '0.8rem' }}>{formatTimeAgo(story.created_at)}</div>
                </div>
                {user && user.id === story.author_id && (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => startEdit(story)} style={{ background: 'none', border: 'none', color: '#8b9cb3', cursor: 'pointer', fontSize: '0.8rem' }}>Edit</button>
                    <button onClick={() => handleDelete(story.id)} style={{ background: 'none', border: 'none', color: '#ff6b6b', cursor: 'pointer', fontSize: '0.8rem' }}>Delete</button>
                  </div>
                )}
              </div>

              {/* Race context */}
              {story.race_name && (
                <div style={{
                  display: 'inline-block',
                  background: 'rgba(255, 138, 61, 0.1)',
                  color: '#FF8A3D',
                  padding: '0.2rem 0.6rem',
                  borderRadius: '6px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  marginBottom: '0.75rem'
                }}>
                  {story.race_name}
                </div>
              )}

              {/* Title */}
              {story.title && (
                <h4 style={{ color: '#FFFFFF', margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>
                  {story.title}
                </h4>
              )}

              {/* Content */}
              <p style={{
                color: '#d1d5db',
                fontSize: '0.95rem',
                lineHeight: 1.7,
                margin: '0 0 0.75rem 0',
                whiteSpace: 'pre-wrap'
              }}>
                {story.content}
              </p>

              {/* Photo */}
              {story.photo_url && (
                <div style={{
                  borderRadius: '10px',
                  overflow: 'hidden',
                  marginTop: '0.5rem'
                }}>
                  <img
                    src={story.photo_url}
                    alt="Race story"
                    style={{
                      width: '100%',
                      maxHeight: '400px',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RaceStories;
